import { invoiceOutstandingMinor } from "../../shared/invoice-settlement";
import { useEffect, useId, useState, type RefObject } from "react";

import { paymentEligibility } from "./BatchPaymentModal";
import { formatMoney, type InvoiceDraftRecord } from "./invoiceApi";

export function InvoicePaymentPicker({ invoices, search, selectedId, inputRef, onSearch, onChoose, onCancel }: {
  invoices: InvoiceDraftRecord[];
  search: string;
  selectedId: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onSearch(value: string): void;
  onChoose(invoice: InvoiceDraftRecord): void;
  onCancel(): void;
}) {
  const id = useId();
  const listId = `${id}-invoices`;
  const [active, setActive] = useState(-1);
  const activeInvoice = invoices[active];

  useEffect(() => { inputRef.current?.focus(); }, [inputRef]);
  useEffect(() => {
    if (activeInvoice) document.getElementById(`${listId}-${activeInvoice.id}`)?.scrollIntoView({ block: "nearest" });
  }, [activeInvoice, listId]);

  function choose(invoice: InvoiceDraftRecord) {
    if (!paymentEligibility(invoice)) onChoose(invoice);
  }

  return <div className="payment-invoice-picker">
    <label className="field" htmlFor={id}>
      <span>Search invoices</span>
      <input ref={inputRef} id={id} className="field-control input" type="search" role="combobox" aria-label="Search invoices" aria-autocomplete="list" aria-expanded="true" aria-controls={listId} aria-describedby={`${id}-hint`} aria-activedescendant={activeInvoice ? `${listId}-${activeInvoice.id}` : undefined} autoComplete="off" placeholder="Client, invoice number, or description" value={search}
        onChange={event => { onSearch(event.target.value); setActive(0); }}
        onKeyDown={event => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setActive(current => invoices.length ? current < 0 ? event.key === "ArrowDown" ? 0 : invoices.length - 1 : Math.max(0, Math.min(invoices.length - 1, current + (event.key === "ArrowDown" ? 1 : -1))) : -1);
          } else if (event.key === "Enter" && activeInvoice) {
            event.preventDefault(); choose(activeInvoice);
          } else if (event.key === "Escape") {
            event.preventDefault(); onCancel();
          }
        }} />
      <small id={`${id}-hint`} className="field-hint">Eligible invoices appear first. Other invoices show why payment is unavailable.</small>
    </label>
    <div id={listId} className="payment-invoice-results" role="listbox" aria-label="Invoices">
      {invoices.map((invoice, index) => {
        const reason = paymentEligibility(invoice);
        const description = invoice.lines.find(line => line.description.trim())?.description;
        return <button key={invoice.id} id={`${listId}-${invoice.id}`} type="button" role="option" tabIndex={-1} aria-selected={selectedId === invoice.id} aria-disabled={Boolean(reason)} className={`client-row payment-invoice-result${active === index ? " is-active" : ""}`} onMouseEnter={() => setActive(index)} onMouseDown={event => event.preventDefault()} onClick={() => { setActive(index); choose(invoice); }}>
          <span><span className="payment-invoice-identity"><strong>{invoice.number ?? "Draft"}</strong> · <span>{invoice.clientName ?? "Client not selected"}</span></span><span className="payment-invoice-balance">{reason ?? `${formatMoney(invoiceOutstandingMinor(invoice))} outstanding`}</span>{description ? <small className="payment-invoice-description">{description}</small> : null}</span>
        </button>;
      })}
    </div>
  </div>;
}
